import React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '~src/codegen/Localize';

import { NavStyles } from './NavigationStyles';
import { NavLocation } from './NavLocation';

export const NavListTestId = 'nav-panel-list';

export interface NavListItemState {
  /** Whether the item should be in the tab order or not */
  readonly tabbable: boolean;
  /** Whether the item should appear as being selected or not */
  readonly selected: boolean;
}

export type NavListItemRenderProp = (navListItemState: NavListItemState) => JSX.Element;

export interface NavListItem {
  /** Value of the item, should be either a `NavLocation` or an empty string for non-selectable (i.e. separator) item */
  readonly value: NavLocation | '';
  /** Render method for the item */
  readonly content: NavListItemRenderProp;
}

export interface NavListProps {
  /** Items in the nav list */
  readonly items: NavListItem[];
  /** Value of the item that should appear as being selected */
  readonly selectedValue: NavLocation;
  /** Method to be called when an item is selected */
  readonly onSelect: (value: NavLocation) => void;
}

export interface NavListState {
  /** Index of the item that should be able to receive focus */
  readonly focusableItemIndex: number;
  /** Whether the nav list (or its descendant) is in focus or not */
  readonly focusWithin: boolean;
}

export class NavList extends React.PureComponent<NavListProps, NavListState> {
  private listElementRef: React.RefObject<HTMLUListElement> = React.createRef();

  private memoizedGetIndicesForFocusManagement = defaultMemoize((items: NavListItem[], selectedValue: NavLocation) => {
    let selectedItemIndex = -1;
    const selectableItemsIndices = items.reduce((indices, item, index) => {
      if (item.value.length) {
        // exclude any item with empty value, which is assumed to be non-selectable (i.e. a separator)
        indices.push(index);
        if (item.value === selectedValue) {
          selectedItemIndex = index;
        }
      }
      return indices;
    }, [] as number[]);

    return { selectableItemsIndices, selectedItemIndex };
  });

  public constructor(props: NavListProps) {
    super(props);
    const { selectableItemsIndices, selectedItemIndex } =
      this.memoizedGetIndicesForFocusManagement(props.items, props.selectedValue);
    this.state = {
      focusableItemIndex: selectedItemIndex === -1 ? selectableItemsIndices[0] : selectedItemIndex,
      focusWithin: false
    };
  }

  public componentDidUpdate(prevProps: NavListProps, prevState: NavListState) {
    if (prevProps.items !== this.props.items || prevProps.selectedValue !== this.props.selectedValue) {
      // when these props change, re-calculate focusableItemIndex
      this.resetFocusableItemIndex();
    }

    if (prevState.focusableItemIndex !== this.state.focusableItemIndex && this.state.focusWithin) {
      // trigger focus to respect the new focusableItemIndex;
      // this does depend on the focusable item content (see `NavLink`) having an anchor element
      this.listElementRef.current.querySelector<HTMLAnchorElement>(
        `li[data-itemindex="${this.state.focusableItemIndex}"] > a`
      )?.focus();
    }
  }

  private resetFocusableItemIndex = (): void => {
    const { selectableItemsIndices, selectedItemIndex } =
      this.memoizedGetIndicesForFocusManagement(this.props.items, this.props.selectedValue);
    this.setState({
      focusableItemIndex: selectedItemIndex === -1 ? selectableItemsIndices[0] : selectedItemIndex
    });
  };

  public render() {
    const { selectedItemIndex } = this.memoizedGetIndicesForFocusManagement(this.props.items, this.props.selectedValue);
    return (
      // this looks and feels like a vertical tablist, but functionally and structurally it's a navigation link list, so we're ok with the mixed paradigm here
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <ul
        data-tb-test-id={NavListTestId}
        ref={this.listElementRef}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        onClick={this.handleClick}
        className={NavStyles.NavList}
        aria-label={Messages.navigationListAriaLabel()}
      >
        {this.props.items.map((item, index) => (
          <li
            data-itemvalue={item.value}
            data-itemindex={index}
            key={index}
            // for a non-selectable (i.e. separator) item, use role=none to hide it from assistive technology;
            // for a selectable item, no need to set any role as it would have the implied listitem role
            role={item.value.length ? undefined : 'none'}
          >
            {item.content({
              tabbable: index === this.state.focusableItemIndex,
              selected: index === selectedItemIndex
            })}
          </li>
        ))}
      </ul>
    );
  }

  private handleFocus = (e: React.FocusEvent<HTMLElement>): void => {
    this.setState({ focusWithin: true });
  };

  private handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    if (e.relatedTarget && !this.listElementRef.current.contains(e.relatedTarget as any)) {
      this.setState({ focusWithin: false });
      this.resetFocusableItemIndex();
    }
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const { selectableItemsIndices } = this.memoizedGetIndicesForFocusManagement(this.props.items, this.props.selectedValue);
    const indexWithinSelectableItems = selectableItemsIndices.indexOf(this.state.focusableItemIndex);

    switch (e.key) {
      case 'ArrowUp':
        if (indexWithinSelectableItems === 0) {
          // when focus is on the first selectable item, move focus to the last selectable item
          this.setState({ focusableItemIndex: selectableItemsIndices[selectableItemsIndices.length-1] });
        } else {
          // move focus to the previous selectable item
          this.setState({ focusableItemIndex: selectableItemsIndices[indexWithinSelectableItems-1] });
        }
        e.stopPropagation();
        e.preventDefault();
        break;
      case 'ArrowDown':
        if (indexWithinSelectableItems === selectableItemsIndices.length-1) {
          // when focus is on the last selectable item, move focus to the first selectable item
          this.setState({ focusableItemIndex: selectableItemsIndices[0] });
        } else {
          // move focus to the next selectable item
          this.setState({ focusableItemIndex: selectableItemsIndices[indexWithinSelectableItems+1] });
        }
        e.stopPropagation();
        e.preventDefault();
        break;
      case 'Enter':
        const listItemInFocus: HTMLElement = this.listElementRef.current.querySelector<HTMLLIElement>(`li[data-itemindex="${this.state.focusableItemIndex}"]`);
        this.props.onSelect(listItemInFocus.getAttribute('data-itemvalue') as NavLocation);
        listItemInFocus.querySelector<HTMLAnchorElement>('a')?.click();
        e.stopPropagation();
        e.preventDefault();
        break;
      default:
        return;
    }
  };

  private handleClick = (e: React.SyntheticEvent<HTMLElement>): void => {
    const listItemClicked = (e.target as HTMLElement).closest('li');
    const value = listItemClicked.getAttribute('data-itemvalue');
    if (value.length) {
      // ignore clicking on any item with empty value, which is assumed to be non-selectable (i.e. a separator)
      this.props.onSelect(value as NavLocation);
    }
  };
}
